import React, { useState, useEffect, useRef } from 'react';
import { Clock } from 'lucide-react';

/**
 * CallDurationTimer - Displays the elapsed time since the meeting started
 * Positioned at the bottom left of the meeting view
 */
export default function CallDurationTimer({ startTime, inline = false }) {
  const [elapsedMs, setElapsedMs] = useState(0);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!startTime) return;

    const updateTimer = () => {
      const now = Date.now();
      setElapsedMs(now - startTime);
      animationRef.current = requestAnimationFrame(updateTimer);
    };

    // Start the animation loop
    animationRef.current = requestAnimationFrame(updateTimer);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [startTime]);

  // Format milliseconds to MM:SS or HH:MM:SS if over an hour
  const formatDuration = (ms) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  if (!startTime) return null;

  return (
    <div className={inline
      ? "flex items-center gap-2 px-3 py-1.5 bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-full"
      : "flex items-center gap-2 px-3 py-1.5"
    }>
      <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
      <Clock className="w-3.5 h-3.5 text-white/60" />
      <span className="text-white text-sm font-medium tabular-nums tracking-wide">
        {formatDuration(elapsedMs)}
      </span>
    </div>
  );
}
